import React from "react";

/**
 * Troncature dynamique d'une barre de navigation horizontale (« priority+ »).
 *
 * Partagé par les thèmes Travel et 2026 (header en pill flottant, mêmes classes
 * .tr-*). La nav rend TOUS les liens de 1er niveau ; ce hook masque ceux qui ne
 * tiennent pas dans la largeur disponible, en partant de la fin (les derniers =
 * les moins prioritaires). Les liens masqués restent accessibles via le burger
 * (toujours affiché) + le drawer, qui listent l'intégralité du menu.
 *
 * Combiné à la classe .tr-header--wide (header étiré à 90vw quand le menu est
 * grand) : on étire d'abord pour afficher un max de liens, PUIS on tronque le
 * surplus éventuel sur les écrans trop étroits.
 *
 * @param {object}  navRef     ref vers l'élément <nav> (conteneur des liens)
 * @param {*}       deps       valeur qui, si elle change, force un recalcul
 *                             (ex: signature du menu, langue) — la mesure se
 *                             refait aussi à chaque resize via ResizeObserver.
 */
export function useOverflowNav(navRef, deps) {
    React.useEffect(() => {
        const nav = navRef.current;
        if (!nav || typeof window === "undefined") return;

        // Marge de sécurité : quelques px libres pour absorber les arrondis
        // sub-pixel et éviter qu'un lien « presque dedans » ne touche le bord.
        const SAFETY = 4;

        let rafId = null;

        const measure = () => {
            rafId = null;
            const items = Array.from(nav.children);
            if (items.length === 0) return;

            // 1) Tout réafficher pour mesurer les largeurs naturelles.
            for (const el of items) el.style.display = "";

            // 2) Largeur réellement disponible + gap résolu (clamp → px).
            const available = nav.clientWidth - SAFETY;
            const cs = getComputedStyle(nav);
            const gap = parseFloat(cs.columnGap || cs.gap || "0") || 0;

            // 3) On cumule ; dès qu'un lien déborde, on le masque lui et les suivants.
            let used = 0;
            let overflowing = false;
            for (let i = 0; i < items.length; i++) {
                const w = items[i].getBoundingClientRect().width;
                const next = used + (i > 0 ? gap : 0) + w;
                if (!overflowing && next <= available) {
                    used = next;
                } else {
                    overflowing = true;
                    items[i].style.display = "none";
                }
            }
        };

        // On mesure dans un rAF : le layout (largeur du header --wide à 90vw,
        // colonne grid) doit être stabilisé avant de lire les largeurs. Mesurer
        // en synchrone juste après un resize donne des valeurs transitoires.
        const schedule = () => {
            if (rafId != null) return;
            rafId = window.requestAnimationFrame(measure);
        };

        schedule();

        // Recalcul quand la nav change de taille (resize fenêtre, orientation…).
        const ro = new ResizeObserver(schedule);
        ro.observe(nav);

        return () => {
            if (rafId != null) window.cancelAnimationFrame(rafId);
            ro.disconnect();
            // Nettoyage : réafficher tout pour ne pas laisser d'items masqués
            // si la nav est ré-utilisée (navigation SPA sans démontage).
            const nav2 = navRef.current;
            if (nav2) for (const el of Array.from(nav2.children)) el.style.display = "";
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [navRef, deps]);
}
